import { createFileRoute, Link } from "@tanstack/react-router";
import { PageBreadcrumb } from "@/components/site/PageBreadcrumb";
import { XCircle, Lightbulb, ArrowRight } from "lucide-react";

const URL = "https://rajshamani.life/failure-resume";

const failures = [
  {
    year: "2014",
    title: "First content channel — abandoned",
    category: "Content",
    what: "Launched a motivational YouTube channel in college. Posted for months. Audience stayed flat.",
    lesson:
      "Don't copy a format you admire — find an angle only you can own. The channel failed because nothing about it was specifically mine.",
  },
  {
    year: "2017",
    title: "Early D2C experiment — shut down",
    category: "Ventures",
    what: "Tested a consumer product line under the family manufacturing umbrella. Margins worked on paper, distribution didn't.",
    lesson:
      "Manufacturing strength is not a distribution moat. Owning the factory means nothing if you can't get the product to the shelf the customer is already standing in front of.",
  },
  {
    year: "2019",
    title: "Speaker bureau pitch — rejected",
    category: "Speaking",
    what: "Pitched a structured corporate speaking circuit before the audience numbers justified the fee. Most decks went unanswered.",
    lesson:
      "Pricing precedes positioning only if the proof is already there. Build the proof in public first; the inbound replaces the cold pitch.",
  },
  {
    year: "2020",
    title: "Podcast v1 — quiet failure",
    category: "Podcast",
    what: "Started a short-form interview show before Figuring Out. Twelve episodes, modest reach, never found a loop.",
    lesson:
      "Long-form works when the conversation is allowed to breathe. Cutting to 15 minutes killed the only thing the format had going for it.",
  },
  {
    year: "2021",
    title: "Investments that didn't return",
    category: "Investing",
    what: "Multiple early cheques into companies that ran out of runway, pivoted away from the original thesis, or shut down quietly.",
    lesson:
      "A great founder pitch in a meeting is not the same as a great founder under pressure. Reference the operators, not just the deck.",
  },
  {
    year: "2022",
    title: "A brand collaboration I should have walked away from",
    category: "Brand",
    what: "Took on a paid collaboration that didn't fit the audience. Engagement tanked, trust took a hit.",
    lesson:
      "Money is the easiest yes and the most expensive one. The audience pays attention to who you stand next to, every time.",
  },
];

export const Route = createFileRoute("/failure-resume")({
  head: () => ({
    meta: [
      { title: "Failure Resume — Raj Shamani", key: "title" },
      {
        name: "description",
        content:
          "Raj Shamani's failure resume — the ventures, investments and projects that didn't work, and the lessons each one left behind.",
        key: "description",
      },
      { property: "og:title", content: "Failure Resume — Raj Shamani", key: "og:title" },
      {
        property: "og:description",
        content:
          "Raj Shamani's failure resume — the ventures, investments and projects that didn't work, and the lessons each one left behind.",
        key: "og:description",
      },
      { property: "og:url", content: URL, key: "og:url" },
      { property: "og:type", content: "article", key: "og:type" },
      { name: "twitter:card", content: "summary_large_image", key: "twitter:card" },
      { name: "twitter:title", content: "Failure Resume — Raj Shamani", key: "twitter:title" },
      {
        name: "twitter:description",
        content:
          "Raj Shamani's failure resume — the ventures, investments and projects that didn't work, and the lessons each one left behind.",
        key: "twitter:description",
      },
    ],
    links: [{ rel: "canonical", href: URL }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Article",
          headline: "Failure Resume — Raj Shamani",
          author: { "@type": "Person", name: "Raj Shamani" },
          mainEntityOfPage: URL,
          description:
            "Raj Shamani's failure resume — the ventures, investments and projects that didn't work, and the lessons each one left behind.",
        }),
      },
    ],
  }),
  component: FailureResume,
});

function FailureResume() {
  return (
    <div>
      <PageBreadcrumb current="Failure Resume" />

      <section className="bg-noir border-b border-border/50">
        <div className="mx-auto max-w-5xl px-6 py-24">
          <div className="text-xs tracking-widest uppercase text-gold mb-4">
            Transparency
          </div>
          <h1 className="text-5xl md:text-6xl font-bold leading-tight">
            My <span className="text-gradient-gold">Failure</span> Resume
          </h1>
          <p className="mt-6 text-lg text-muted-foreground max-w-2xl">
            Everyone publishes the wins. This is the other list — the ventures,
            investments, pitches and projects that didn't work, and the lesson
            each one left behind.
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-4xl px-6 py-24">
        <div className="flex items-center gap-3 mb-12">
          <XCircle className="text-gold" />
          <h2 className="text-2xl md:text-3xl font-bold">Things that didn't work</h2>
        </div>

        <ol className="space-y-12">
          {failures.map((f) => (
            <li
              key={f.title}
              className="border-l border-gold/40 pl-6 md:pl-8"
            >
              <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1 mb-3">
                <span className="text-sm font-mono text-gold tabular-nums">
                  {f.year}
                </span>
                <span className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground">
                  {f.category}
                </span>
              </div>
              <h3 className="text-xl md:text-2xl font-display font-semibold tracking-tight">
                {f.title}
              </h3>
              <p className="mt-3 text-muted-foreground leading-relaxed">
                {f.what}
              </p>
              <div className="mt-4 flex gap-3 items-start rounded-sm border border-border bg-noir/40 p-4">
                <Lightbulb
                  className="text-gold mt-0.5 shrink-0"
                  size={18}
                  strokeWidth={1.5}
                />
                <p className="text-sm text-foreground leading-relaxed">
                  <span className="text-gold font-medium">Lesson:</span>{" "}
                  {f.lesson}
                </p>
              </div>
            </li>
          ))}
        </ol>
      </section>

      <section className="border-y border-border bg-noir/30">
        <div className="mx-auto max-w-3xl px-6 py-20 text-center">
          <h2 className="text-3xl md:text-4xl font-display font-semibold tracking-tight">
            Why publish this?
          </h2>
          <p className="mt-6 text-muted-foreground leading-relaxed">
            Because the highlight reel lies. Every founder I admire has a list
            twice as long as this one — they just don't publish it. The point
            isn't to celebrate failure; it's to make the lesson cheap enough
            for the next person to learn without paying for it again.
          </p>
          <Link
            to="/podcast"
            className="mt-10 inline-flex items-center gap-2 px-6 py-3 rounded-sm bg-gold text-primary-foreground font-medium hover:opacity-90 transition"
          >
            Hear founders unpack theirs on Figuring Out
            <ArrowRight size={16} />
          </Link>
        </div>
      </section>
    </div>
  );
}
